import type { TeacherConversationDetailResource } from "api/generated";
import { mapAttachments, mapLinks, mapVideos } from "shared/mappers";
import type { TeacherConversationDetail } from "conversations/types";
import { parseSchoolDateTime } from "shared/utils";
import { studentConversationQuestionFromDto } from "./studentConversationDetail.mapper";

/**
 * Maps `GET api/conversations/{conversation}` — one gesprek as its docent sees it.
 *
 * `date` carries a Dutch wall-clock moment, so it is read through the school clock rather
 * than the browser's timezone — same as every other gesprek mapper.
 *
 * Both notes columns arrive as null while nothing is written and are mapped to "": the
 * Gespreksnotitie block renders one as text and binds the other to a textarea, and neither
 * wants a null check for "not written yet".
 */
export const teacherConversationDetailFromDto = (
  dto: TeacherConversationDetailResource,
): TeacherConversationDetail => ({
  id: dto.id,
  name: dto.name,
  date: parseSchoolDateTime(dto.date),
  endDate: parseSchoolDateTime(dto.end_date),
  conversationType: {
    id: dto.conversation_type.id,
    name: dto.conversation_type.name,
  },
  student: {
    id: dto.student.id,
    userId: dto.student.user_id,
    name: dto.student.name,
    firstName: dto.student.first_name,
    avatar: dto.student.avatar,
  },
  teacher:
    dto.teacher === null
      ? null
      : { name: dto.teacher.name, avatar: dto.teacher.avatar },
  subjectShortage: dto.subject_shortage ?? null,
  teacherShortage: dto.teacher_shortage ?? null,
  isNote: dto.is_note,
  canEdit: dto.can_edit,
  canDelete: dto.can_delete,
  allowsPreparation: dto.allows_preparation,
  needsPreparation: dto.needs_preparation,
  isPrepared: dto.is_prepared,
  status: dto.status,
  preparedAt: parseSchoolDateTime(dto.prepared_at),
  description: dto.description ?? null,
  teacherNotes: dto.teacher_notes ?? "",
  studentNotes: dto.student_notes ?? "",
  teacherNotesUpdatedAt: parseSchoolDateTime(dto.teacher_notes_updated_at),
  studentNotesUpdatedAt: parseSchoolDateTime(dto.student_notes_updated_at),
  // Already ordered by the API; mapped straight through so the docent reads their vragen
  // back in the order they asked them.
  questions: dto.questions.map(studentConversationQuestionFromDto),
  attachments: mapAttachments(dto.attachments),
  links: mapLinks(dto.links),
  videos: mapVideos(dto.videos),
  attachmentAllowed: Boolean(dto.attachment_allowed),
  attachmentRequired: Boolean(dto.attachment_required),
  preparationAttachments: mapAttachments(dto.preparation_attachments),
  preparationLinks: mapLinks(dto.preparation_links),
  preparationVideos: mapVideos(dto.preparation_videos),
  invitationAttachments: mapAttachments(dto.invitation_attachments),
  invitationLinks: mapLinks(dto.invitation_links),
  invitationVideos: mapVideos(dto.invitation_videos),
});
